import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSupport } from '@/hooks/useSupport';
import { useAuth } from '@/hooks/useAuth';
import { LandingHeader } from '@/components/landing/LandingHeader';
import Footer from '@/components/Footer';

const Support = () => {
  const navigate = useNavigate();
  const { sendSupportEmail, isLoading } = useSupport();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: '',
    subjectType: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await sendSupportEmail(formData);
    if (success) {
      setFormData({ 
        email: user?.email || '', 
        name: '', 
        subjectType: '', 
        subject: '', 
        message: '' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LandingHeader />
      <div className="container max-w-2xl mx-auto px-4 py-8 pt-24 flex-1">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>

        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Support</h1>
            <p className="text-muted-foreground">
              Une question ? Un bug à signaler ? Nous sommes là pour vous aider.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!user && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nom (optionnel)</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    disabled={isLoading}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="subjectType">Type de demande</Label>
              <Select
                value={formData.subjectType}
                onValueChange={(value) =>
                  setFormData({ ...formData, subjectType: value })
                }
                required
                disabled={isLoading}
              >
                <SelectTrigger id="subjectType">
                  <SelectValue placeholder="Sélectionnez un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">🐛 Signaler un bug</SelectItem>
                  <SelectItem value="facturation">💳 Facturation</SelectItem>
                  <SelectItem value="fonctionnalite">✨ Demande de fonctionnalité</SelectItem>
                  <SelectItem value="compte">👤 Gestion du compte</SelectItem>
                  <SelectItem value="autre">❓ Autre question</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Sujet</Label>
              <Input
                id="subject"
                type="text"
                placeholder="Ex: Problème de synchronisation"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Décrivez votre problème ou votre question en détail..."
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                required
                disabled={isLoading}
                rows={8}
                className="resize-none"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Envoi en cours...' : 'Envoyer'}
            </Button>
          </form>

          <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-2">
            <h2 className="font-semibold">Temps de réponse</h2>
            <p className="text-sm text-muted-foreground">
              Nous nous efforçons de répondre à toutes les demandes dans un délai de 24 à 48 heures ouvrées.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Support;
